<script setup lang="ts">
import { onMounted, ref } from "vue";
import { addressIntoGMapsLink, bemBuilder } from "@chatfood/core-utils";
import {
  SatButton,
  AtomLoadingSpinner,
  AtomText,
} from "@chatfood/design-system";
import { useBugReporter, useBusinessStore, useDirectBackend } from "#imports";
import { useLocale } from "~/composables/use-locale";

const css = bemBuilder("outlet-details");
const props = defineProps<{
  outletId: string;
}>();

const { t } = useLocale();
const business = useBusinessStore();
const { outletRepo } = useDirectBackend();
const { report } = useBugReporter();
const isLoading = ref(false);

type ResponseOpeningHours = Awaited<
  ReturnType<typeof outletRepo.getSettings>
>[0]["openingHours"];

type GroupedOpeningHour = {
  dayOfWeek: string;
  times: { start: string; end: string }[];
};

const openingHoursDelivery = ref<GroupedOpeningHour[]>([]);
const openingHoursPickup = ref<GroupedOpeningHour[]>([]);
const orderingModeSettingsDelivery = ref();
const orderingModeSettingsPickup = ref();

const orderedDays: Record<string, string> = {
  Mon: t("components.outlet_details.days_of_week.monday"),
  Tue: t("components.outlet_details.days_of_week.tuesday"),
  Wed: t("components.outlet_details.days_of_week.wednesday"),
  Thu: t("components.outlet_details.days_of_week.thursday"),
  Fri: t("components.outlet_details.days_of_week.friday"),
  Sat: t("components.outlet_details.days_of_week.saturday"),
  Sun: t("components.outlet_details.days_of_week.sunday"),
};

const dayOrder = Object.keys(orderedDays);

const groupOpeningHours = (
  openingHours: ResponseOpeningHours,
): GroupedOpeningHour[] => {
  const map = new Map<string, { start: string; end: string }[]>();

  for (const { dayOfWeek, startTime, endTime } of openingHours) {
    const times = map.get(dayOfWeek) ?? [];
    times.push({ start: startTime, end: endTime });
    map.set(dayOfWeek, times);
  }

  const groupedDelivery = Array.from(map.entries()).map(
    ([dayOfWeek, times]) => ({
      dayOfWeek,
      times: times.sort((cur, acc) => cur.start.localeCompare(acc.start)),
    }),
  );

  return [...groupedDelivery]
    .sort(
      (cur, acc) =>
        dayOrder.indexOf(cur.dayOfWeek) - dayOrder.indexOf(acc.dayOfWeek),
    )
    .map(({ dayOfWeek, ...rest }) => ({
      dayOfWeek: orderedDays[dayOfWeek],
      ...rest,
    }));
};

const onGetDirections = () => {
  if (!outlet.value) return;
  const link = addressIntoGMapsLink({
    lat: outlet.value.address.coordinate.lat,
    lng: outlet.value.address.coordinate.lng,
  });

  window.open(link, "_blank");
};

const onCallStore = () => {
  if (!outlet.value || !outlet.value.phoneNumber) return;
  const phoneNumber = outlet.value.phoneNumber.replace(/\s/g, "");
  window.open(`tel:${phoneNumber}`, "_self");
};

const outlet = ref<Awaited<ReturnType<typeof outletRepo.getOutletById>>>();
onMounted(async () => {
  isLoading.value = true;
  try {
    const [outletDetails, orderingModeSettings] = await Promise.all([
      outletRepo.getOutletById({
        outletId: props.outletId,
        salesChannel: business.salesChannel,
        orderingMode: "delivery",
      }),
      outletRepo.getSettings({
        outletId: props.outletId,
        salesChannel: business.salesChannel,
        orderingMode: ["delivery", "pickup"],
      }),
    ]);

    outlet.value = outletDetails;

    orderingModeSettingsDelivery.value = orderingModeSettings.find(
      (setting: any) => setting.mode === "delivery",
    );

    orderingModeSettingsPickup.value = orderingModeSettings.find(
      (setting: any) => setting.mode === "pickup",
    );

    openingHoursDelivery.value = groupOpeningHours(
      orderingModeSettings.find((setting: any) => setting.mode === "delivery")
        .openingHours,
    );
    openingHoursPickup.value = groupOpeningHours(
      orderingModeSettings.find((setting: any) => setting.mode === "pickup")
        .openingHours,
    );
  } catch (e) {
    report(e);
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div :class="css()">
    <AtomLoadingSpinner v-if="!outlet || isLoading" :class="css('loading')" />
    <template v-else>
      <div :class="css('outlet-details-content')">
        <AtomText
          :class="css('title')"
          data-test="outlet-name"
          type="subtitle"
          color="color-text-body"
        >
          {{ outlet.name }}
        </AtomText>
        <AtomText color="color-text-body" data-test="outlet-address">
          {{ outlet.address.location }}
        </AtomText>
        <div
          v-if="orderingModeSettingsDelivery.isEnabled"
          data-test="delivery-hours"
        >
          <div :class="css('separator')" />
          <AtomText
            v-if="openingHoursDelivery.length"
            :class="css('opening-hours-title')"
            color="color-text-body"
            type="body-bold"
          >
            {{ t("components.outlet_details.delivery_hours") }}
          </AtomText>
          <ul :class="css('list')">
            <li
              v-for="slot in openingHoursDelivery"
              :key="slot.dayOfWeek"
              :class="css('list-item')"
            >
              <AtomText color="color-text-secondary">{{
                slot.dayOfWeek
              }}</AtomText>
              <AtomText
                v-for="(time, index) in slot.times"
                :key="`time-${index}`"
                color="color-text-secondary"
              >
                {{ time.start }} - {{ time.end }}
              </AtomText>
            </li>
          </ul>
        </div>
        <div
          v-if="orderingModeSettingsPickup.isEnabled"
          data-test="pickup-hours"
        >
          <div
            v-if="openingHoursPickup.length && openingHoursDelivery.length"
            :class="css('separator')"
          />
          <AtomText
            v-if="openingHoursPickup.length"
            :class="css('opening-hours-title')"
            color="color-text-body"
            type="body-bold"
          >
            {{ t("components.outlet_details.pickup_hours") }}
          </AtomText>
          <ul :class="css('list')">
            <li
              v-for="slot in openingHoursPickup"
              :key="slot.dayOfWeek"
              :class="css('list-item')"
            >
              <AtomText color="color-text-secondary">{{
                slot.dayOfWeek
              }}</AtomText>
              <AtomText
                v-for="(time, index) in slot.times"
                :key="`time-${index}`"
                color="color-text-secondary"
              >
                {{ time.start }} - {{ time.end }}
              </AtomText>
            </li>
          </ul>
        </div>
      </div>
      <div :class="css('ctas')">
        <div
          v-if="openingHoursDelivery.length || openingHoursPickup.length"
          :class="css('separator')"
        />
        <SatButton
          v-if="outlet.phoneNumber"
          variant="primary"
          type="button"
          theme="ghost"
          size="large"
          @click="onCallStore"
        >
          {{ t("components.outlet_details.call_store_cta") }}
        </SatButton>
        <SatButton
          variant="primary"
          type="button"
          theme="ghost"
          size="large"
          @click="onGetDirections"
        >
          {{ t("components.outlet_details.get_directions_cta") }}
        </SatButton>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.outlet-details {
  display: flex;
  flex-direction: column;
  max-width: 100%;
  width: 640px;
  min-height: 400px;
  position: relative;

  &__outlet-details-content {
    padding: var(--spacing-lg) var(--spacing-lg) 0 var(--spacing-lg);

    @media (width > $breakpoint-sm) {
      padding: 0;
    }
  }

  &__title {
    margin-bottom: var(--spacing-md);
  }

  &__opening-hours-title {
    margin-bottom: var(--spacing-sm);
  }

  &__list {
    list-style: none;
    padding-left: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
  }

  &__list-item {
    display: flex;
    gap: var(--spacing-sm);
    margin-top: var(--spacing-xs);
  }

  &__loading {
    position: absolute;
    left: calc(50% - 25px);
    top: calc(50% - 25px);
  }

  &__box {
    width: 100%;
    min-height: 51px;
    border-radius: var(--radii-card-sm);
    border: 1px solid var(--color-base-90);
    background: var(--color-base-100);

    &--disabled {
      pointer-events: none;
      cursor: no-drop;
      background: var(--color-base-95);
    }
  }

  &__open-selection {
    display: block;
    padding: var(--spacing-md);
  }

  &__content {
    display: flex;
    gap: 14px;
    align-items: center;
    justify-content: space-between;
  }

  &__selection {
    max-width: 100%;
    min-height: 30dvh;

    @media (width >= $breakpoint-sm) {
      width: 470px;
    }
  }

  &__separator {
    height: 1px;
    background-color: var(--color-base-90);
    margin: var(--spacing-md) 0;
  }

  &__ctas {
    display: flex;
    gap: var(--spacing-sm);
    flex-direction: column;
    position: sticky;
    bottom: 0;
    background-color: var(--color-base-100);
    padding: 0 var(--spacing-lg) var(--spacing-lg) var(--spacing-lg);

    @media (width > $breakpoint-sm) {
      padding: 0;
      position: inherit;
    }
  }
}
</style>
